<template>
    <FormSection title="Payer Information" type="his-section" :full-height="false" :application-id="application">
        <template #content>
            <section class="row py-3 pb-0 g-0">
                <div class="col-12 col-lg-12 px-4 col-md-12 g-0">
                    <a
                        href="https://www.cms.gov/training-education/partner-outreach-resources/new-medicare-card/providers-office-managers"
                        target="_blank"
                        >Click here for CMS guidelines on the new Medicare Beneficiary Identifier (MBI)</a
                    >
                    <p class="mt-2">
                        MBI usage is required for all Medicare patients. Contact the patient or representative to obtain
                        the 11-digit MBI provided on the patient's Medicare card, and enter it in the patient's chart.
                        If the patient is unaware of his/her MBI, your
                        <a href="https://www.cms.gov/mac-info" target="_blank"
                            >Medicare Administrative Contractor's provider portal</a
                        >
                        can be used to look up MBIs.
                    </p>

                    <div class="row">
                        <div class="form-group col-12 col-lg-12 pe-lg-12">
                            <label for="referral-medicare-beneficiary-identifier">
                                <span class="badge me-1">M0063</span> Medicare Beneficiary Identifier (MBI) Number
                                <span class="text-danger">*</span>
                            </label>
                            <span>
                                <div class="input-group d-flex w-100 flex-nowrap">
                                    <div class="flex-grow-1">
                                        <input
                                            name="referral-medicare-beneficiary-identifier"
                                            maxlength="11"
                                            :class="`form-control h-100 no-border-radius ${
                                                v$.referral.Insurance.MbiNumber.$error ||
                                                errors.Insurance.includes('MbiNumber')
                                                    ? 'border border-warning'
                                                    : ''
                                            }`"
                                            type="text"
                                            :disabled="referral.Insurance.IsMbiNA"
                                            v-model="referral.Insurance.MbiNumber"
                                            @blur="v$.referral.Insurance.MbiNumber.$touch"
                                            @change="(e:any)=>handleAllInput('MbiNumber',e.target.value)"
                                        />
                                    </div>
                                    <div class="input-group-prepend flex">
                                        <div class="input-group-text form-check mb-0 rounded-0 px-3">
                                            <input
                                                type="checkbox"
                                                class="form-check-input ms-0 mt-0"
                                                id="referral-medicare-beneficiary-na"
                                                v-model="referral.Insurance.IsMbiNA"
                                                @change="(e:any)=>handleAllInput('MbiNumber',referral.Insurance.IsMbiNA? 'MbiNA': null )"
                                            />
                                            <label class="form-check-label" for="referral-medicare-beneficiary-na">
                                                <small>N/A </small>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </span>
                        </div>
                        <div class="form-group col-12 col-lg-12 pe-lg-12">
                            <label for="referral-health-insurance-claim"> Health Insurance Claim (HIC) Number </label>
                            <span>
                                <div class="input-group d-flex w-100 flex-nowrap">
                                    <div class="flex-grow-1">
                                        <input
                                            name="referral-health-insurance-claim"
                                            class="form-control h-100 no-border-radius"
                                            type="text"
                                            :disabled="referral.Insurance.IsHicNA"
                                            v-model="referral.Insurance.HicNumber"
                                        />
                                    </div>
                                    <div class="input-group-prepend flex">
                                        <div class="input-group-text form-check mb-0 rounded-0 px-3">
                                            <input
                                                type="checkbox"
                                                class="form-check-input ms-0 mt-0"
                                                id="referral-health-insurance-claim-na"
                                                v-model="referral.Insurance.IsHicNA"
                                            />
                                            <label class="form-check-label" for="referral-health-insurance-claim-na">
                                                <small>N/A </small>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </span>
                        </div>
                        <div class="form-group col-12 col-lg-12 pe-lg-12">
                            <label for="referral-medicaid-number">
                                <span class="badge me-1">M0065</span>
                                Medicaid Number
                                <span class="text-danger">*</span>
                            </label>
                            <span>
                                <div class="input-group d-flex w-100 flex-nowrap">
                                    <div class="flex-grow-1">
                                        <input
                                            name="referral-medicaid-number"
                                            :class="`form-control h-100 no-border-radius ${
                                                v$.referral.Insurance.MedicaidNumber.$error ||
                                                errors.Insurance.includes('MedicaidNumber')
                                                    ? 'border border-warning'
                                                    : ''
                                            }`"
                                            type="text"
                                            :disabled="referral.Insurance.IsMedicaidNA"
                                            v-model="referral.Insurance.MedicaidNumber"
                                            @blur="v$.referral.Insurance.MedicaidNumber.$touch"
                                            @change="(e:any)=>handleAllInput('MedicaidNumber',e.target.value)"
                                        />
                                    </div>
                                    <div class="input-group-prepend flex">
                                        <div class="input-group-text form-check mb-0 rounded-0 px-3">
                                            <input
                                                type="checkbox"
                                                class="form-check-input ms-0 mt-0"
                                                id="referral-medicaid-number-na"
                                                v-model="referral.Insurance.IsMedicaidNA"
                                                @change="(e:any)=>handleAllInput('MedicaidNumber',referral.Insurance.IsMedicaidNA? 'MedicaidNA': null )"
                                            />
                                            <label class="form-check-label" for="referral-medicaid-number-na">
                                                <small>N/A </small>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </span>
                        </div>
                        <div class="form-group col-12 col-lg-12 pe-lg-12">
                            <label for="referral-alternate-medicaid-number"> Alternate Medicaid Number </label>
                            <span>
                                <div class="input-group d-flex w-100 flex-nowrap">
                                    <div class="flex-grow-1">
                                        <input
                                            name="referral-alternate-medicaid-number"
                                            class="form-control h-100 no-border-radius"
                                            type="text"
                                            :disabled="referral.Insurance.IsAlternateMedicaidNA"
                                            v-model="referral.Insurance.AlternateMedicaidNumber"
                                        />
                                    </div>
                                    <div class="input-group-prepend flex">
                                        <div class="input-group-text form-check mb-0 rounded-0 px-3">
                                            <input
                                                type="checkbox"
                                                class="form-check-input ms-0 mt-0"
                                                id="referral-alternate-medicaid-number-na"
                                                v-model="referral.Insurance.IsAlternateMedicaidNA"
                                            />
                                            <label class="form-check-label" for="referral-alternate-medicaid-number-na">
                                                <small>N/A </small>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                                  <div class="mt-3">
                                    <button class="btn btn-xs btn-primary p-1"  @click="verifyEligibility()">
                                        <span>Verify Medicare Eligibility</span>
                                    </button>
                                </div>
                            </span>
                        </div>
                        <div class="col-12 py-2">
                            <div class="w-100 my-2 mt-4">
                                <p class="mb-2 d-flex align-items-center">
                                    <b class="me-2"> Primary Insurance</b>
                                </p>
                            </div>
                        </div>
                        <div class="form-group col-12 col-lg-12 pe-lg-12">
                            <label for="referral-primary-insurance"> Primary: <span class="text-danger">*</span></label>
                            <select
                                :class="`form-select ${
                                    v$.referral.Insurance.PrimaryInsurance.$error ||
                                    errors.Insurance.includes('PrimaryInsurance')
                                        ? 'border border-warning'
                                        : ''
                                }`"
                                name="referral-primary-insurance"
                                v-model="referral.Insurance.PrimaryInsurance"
                                @change="setMedicareOrMedicaid(referral.Insurance.PrimaryInsurance, 'primary')"
                                @blur="v$.referral.Insurance.PrimaryInsurance.$touch"
                            >
                                <option :value="null">Select Primary Insurance</option>
                                <option
                                    v-for="(status, index) in referralOptions?.primaryInsurance"
                                    :key="index"
                                    :value="status.value"
                                >
                                    {{ status.text }}
                                </option>
                            </select>
                        </div>

                        <Medicare type="Primary" v-if="isPrimaryMedicare" />
                        <Medicaid type="Primary" :options="referralOptions" v-if="isPrimaryMedicaid" />

                        <div class="col-12 py-2">
                            <div class="w-100 my-2">
                                <p class="mb-2 d-flex align-items-center">
                                    <b class="me-2"> Secondary Insurance</b>
                                </p>
                            </div>
                        </div>
                        <div class="form-group col-12 col-lg-12 pe-lg-12">
                            <label for="referral-secondary-insurance"> Secondary: </label>
                            <select
                                class="form-select"
                                name="referral-secondary-insurance"
                                v-model="referral.Insurance.SecondaryInsurance"
                                @change="setMedicareOrMedicaid(referral.Insurance.SecondaryInsurance, 'secondary')"
                            >
                                <option :value="null">Select Secondary Insurance</option>
                                <option
                                    v-for="(status, index) in referralOptions?.secondaryInsurance"
                                    :key="index"
                                    :value="status.value"
                                >
                                    {{ status.text }}
                                </option>
                            </select>
                        </div>

                        <Medicare type="Secondary" v-if="isSecondaryMedicare" />
                        <Medicaid type="Secondary" :options="referralOptions" v-if="isSecondaryMedicaid" />

                        <div class="col-12 py-2">
                            <div class="w-100 my-2">
                                <p class="mb-2 d-flex align-items-center">
                                    <b class="me-2"> Tertiary Insurance</b>
                                </p>
                            </div>
                        </div>
                        <div class="form-group col-12 col-lg-12 pe-lg-12">
                            <label for="referral-tertiary-insurance"> Tertiary: </label>
                            <select
                                class="form-select"
                                name="referral-tertiary-insurance"
                                v-model="referral.Insurance.TertiaryInsurance"
                                @change="setMedicareOrMedicaid(referral.Insurance.TertiaryInsurance, 'tertiary')"
                            >
                                <option :value="null">Select Tertiary Insurance</option>
                                <option
                                    v-for="(status, index) in referralOptions?.tertiaryInsurance"
                                    :key="index"
                                    :value="status.value"
                                >
                                    {{ status.text }}
                                </option>
                            </select>
                        </div>

                        <Medicare type="Tertiary" v-if="isTertiaryMedicare" />
                        <Medicaid type="Tertiary" :options="referralOptions" v-if="isTertiaryMedicaid" />
                    </div>
                </div>
            </section>
        </template>
    </FormSection>
</template>

<script lang="ts" src="./PayerInformation.ts"></script>
